'use client';

import { useState, useRef, useEffect } from 'react';
import { useI18n } from '@/lib/i18n/context';
import { LOCALES } from '@/locales';
import type { Locale } from '@/locales';

const FLAG_EMOJI: Record<Locale, string> = {
  en: '🇬🇧',
  vi: '🇻🇳',
  zh: '🇨🇳',
  ja: '🇯🇵',
  ko: '🇰🇷',
  ar: '🇸🇦',
  fr: '🇫🇷',
  es: '🇪🇸',
  ru: '🇷🇺',
  hi: '🇮🇳',
  id: '🇮🇩',
  de: '🇩🇪',
  pt: '🇧🇷',
  it: '🇮🇹',
};

export function LanguageSwitcher() {
  const { locale, setLocale, t } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentLocale = LOCALES.find(l => l.code === locale)!;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-3 py-2 rounded-xl hover:bg-surface-800 transition-colors text-gray-400 hover:text-white text-sm"
        aria-label={t.language.label}
        title={t.language.label}
      >
        <span className="text-base leading-none">{FLAG_EMOJI[locale]}</span>
        <span className="hidden sm:inline font-medium">{currentLocale.label}</span>
        <svg
          className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 top-11 w-44 bg-surface-900 border border-surface-700 rounded-2xl shadow-2xl overflow-hidden z-50 animate-scale-in">
          <div className="px-3 py-2 border-b border-surface-700">
            <p className="text-xs text-gray-500 font-medium">{t.language.label}</p>
          </div>
          <div className="py-1">
            {LOCALES.map(({ code, label }) => (
              <button
                key={code}
                onClick={() => { setLocale(code); setOpen(false); }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm transition-colors
                  ${code === locale
                    ? 'bg-brand-600/20 text-brand-400'
                    : 'text-gray-300 hover:bg-surface-800 hover:text-white'
                  }`}
              >
                <span className="text-base">{FLAG_EMOJI[code]}</span>
                <span>{label}</span>
                {code === locale && (
                  <svg className="w-3.5 h-3.5 ml-auto text-brand-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
